import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AlertCircle, Briefcase, Clock3, FileText, Plus, Search, SlidersHorizontal, TicketCheck, UserRound, X } from 'lucide-react'
import { sistema } from '../utils/api'

const SEARCH_ID = 'global-search-results'
const RECENT_KEY = 'aden:global-search:recent'
const EMPTY_RESULTS = { chamados: [], contratos: [], clientes: [], oportunidades: [] }
const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR')

const readRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').slice(0, 5) } catch { return [] }
}

function Highlight({ text, query }) {
  const value = String(text || '')
  const index = normalize(value).indexOf(normalize(query))
  if (!query || index < 0) return value
  return <>{value.slice(0, index)}<mark>{value.slice(index, index + query.length)}</mark>{value.slice(index + query.length)}</>
}

export default function BuscaGlobal() {
  const [q, setQ] = useState('')
  const [results, setResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [recent, setRecent] = useState(readRecent)
  const ref = useRef(null)
  const inputRef = useRef(null)
  const timer = useRef(null)
  const requestSequence = useRef(0)
  const nav = useNavigate()
  const { pathname } = useLocation()
  const [scope, setScope] = useState('all')

  const groups = useMemo(() => {
    const source = results || EMPTY_RESULTS
    return [
      { key: 'chamados', label: 'Chamados', icon: TicketCheck, items: source.chamados || [], href: item => `/chamados/${item.id}`, title: item => item.titulo, meta: item => `${item.codigo} · ${item.cliente || 'Cliente não informado'}` },
      { key: 'contratos', label: 'Contratos', icon: FileText, items: source.contratos || [], href: item => `/contratos/${item.id}`, title: item => item.cliente, meta: item => `${item.codigo} · ${item.status || item.tipo || 'Contrato'}` },
      { key: 'clientes', label: 'Clientes', icon: UserRound, items: source.clientes || [], href: item => `/clientes/${item.id}`, title: item => item.nome, meta: item => item.documento || 'Documento não informado' },
      { key: 'oportunidades', label: 'Oportunidades', icon: Briefcase, items: source.oportunidades || [], href: item => `/crm/oportunidade/${item.id}`, title: item => item.titulo, meta: item => `${item.cliente || 'Prospect'} · ${item.status || 'aberta'}` },
    ].filter(group => scope === 'all' || group.key === scope)
  }, [results, scope])
  const flatResults = useMemo(() => groups.flatMap(group => group.items.map(item => ({ group, item, href: group.href(item) }))), [groups])

  useEffect(() => {
    const onOutside = event => { if (ref.current && !ref.current.contains(event.target)) setOpen(false) }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [])

  useEffect(() => {
    const focusSearch = event => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault(); inputRef.current?.focus(); setOpen(true)
      }
    }
    window.addEventListener('keydown', focusSearch)
    return () => window.removeEventListener('keydown', focusSearch)
  }, [])

  useEffect(() => () => clearTimeout(timer.current), [])

  const remember = term => {
    const normalized = term.trim()
    if (normalized.length < 2) return
    const next = [normalized, ...recent.filter(item => item !== normalized)].slice(0, 5)
    setRecent(next)
    localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  }

  const close = () => { setOpen(false); setActiveIndex(-1) }
  const chooseResult = (href, term=q) => { remember(term); close(); setQ(''); setResults(null); nav(href) }

  const buscar = value => {
    setQ(value); setError(''); setActiveIndex(-1); clearTimeout(timer.current)
    if (value.trim().length < 2) { setResults(null); setLoading(false); setOpen(Boolean(recent.length)); return }
    setOpen(true); setLoading(true)
    const sequence = ++requestSequence.current
    timer.current = setTimeout(async () => {
      try {
        const response = await sistema.busca(value.trim())
        if (sequence === requestSequence.current) setResults(response.data)
      } catch (requestError) {
        if (sequence === requestSequence.current) {
          setResults(EMPTY_RESULTS)
          setError(requestError.response?.data?.detail || 'Não foi possível concluir a busca. Tente novamente.')
        }
      } finally {
        if (sequence === requestSequence.current) setLoading(false)
      }
    }, 300)
  }

  const onKeyDown = event => {
    if (event.key === 'Escape') { close(); inputRef.current?.blur(); return }
    const optionCount = q.trim().length < 2 ? recent.length : flatResults.length
    if (!open || !optionCount) return
    if (event.key === 'ArrowDown') { event.preventDefault(); setActiveIndex(index => (index + 1) % optionCount) }
    if (event.key === 'ArrowUp') { event.preventDefault(); setActiveIndex(index => index <= 0 ? optionCount - 1 : index - 1) }
    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault()
      if (q.trim().length < 2) buscar(recent[activeIndex])
      else chooseResult(flatResults[activeIndex].href)
    }
  }

  const showRecent = open && q.trim().length < 2 && recent.length > 0
  const showPanel = open && (loading || error || results || showRecent)
  const contextualScope = pathname.startsWith('/chamados') ? 'chamados' : pathname.startsWith('/clientes') ? 'clientes' : pathname.startsWith('/contratos') ? 'contratos' : pathname.startsWith('/crm') ? 'oportunidades' : null
  let optionIndex = -1

  return (
    <form ref={ref} className="global-search relative w-full max-w-md" role="search" autoComplete="off" onSubmit={event => event.preventDefault()}>
      <div className="global-search-control">
        <Search size={18} aria-hidden="true"/>
        <input ref={inputRef} value={q} onChange={event => buscar(event.target.value)} onFocus={() => setOpen(true)} onKeyDown={onKeyDown}
          role="combobox" aria-label="Busca global" aria-autocomplete="list" aria-controls={SEARCH_ID} aria-expanded={showPanel}
          aria-activedescendant={activeIndex >= 0 ? `global-search-option-${activeIndex}` : undefined}
          autoComplete="off" inputMode="search" enterKeyHint="search" name="aden-global-search-query" data-form-type="other" data-lpignore="true" data-1p-ignore="true" placeholder="Buscar clientes, chamados e contratos…"/>
        {loading ? <span className="global-search-spinner" aria-label="Buscando"/> : null}
        {q && !loading ? <button type="button" aria-label="Limpar busca" title="Limpar busca" className="touch-target-44 flex items-center justify-center" onClick={() => { setQ(''); setResults(null); setError(''); setOpen(Boolean(recent.length)); inputRef.current?.focus() }}><X size={15}/></button> : <kbd className="hidden md:inline-flex"><span>Ctrl</span>K</kbd>}
      </div>

      {showPanel ? <div id={SEARCH_ID} className="global-search-results" role="listbox" aria-label="Resultados da busca">
        {q.trim().length >= 2 && contextualScope ? <div className="global-search-scope" role="group" aria-label="Escopo da busca"><button type="button" aria-pressed={scope === 'all'} onClick={() => setScope('all')}>Todo o sistema</button><button type="button" aria-pressed={scope === contextualScope} onClick={() => setScope(contextualScope)}>Somente este módulo</button></div> : null}
        {loading ? <div className="global-search-state" role="status"><span className="global-search-spinner"/>Buscando em todo o sistema…</div> : null}
        {!loading && error ? <div className="global-search-state global-search-state--error" role="alert"><AlertCircle size={18}/><span>{error}</span><button type="button" onClick={() => buscar(q)}>Tentar novamente</button></div> : null}
        {!loading && !error && showRecent ? <section className="global-search-group">
          <h2><Clock3 size={14}/>Buscas recentes <button type="button" onClick={() => { setRecent([]); localStorage.removeItem(RECENT_KEY); setOpen(false) }}>Limpar</button></h2>
          {recent.map((term, index) => <button type="button" role="option" aria-selected={activeIndex === index}
            id={`global-search-option-${index}`} key={term} className="global-search-recent"
            onMouseEnter={() => setActiveIndex(index)} onClick={() => buscar(term)}><Search size={14}/><span>{term}</span></button>)}
        </section> : null}
        {!loading && !error && q.trim().length >= 2 ? groups.map(group => group.items.length ? <section className="global-search-group" key={group.key}>
          <h2>{<group.icon size={14} aria-hidden="true"/>}{group.label}<span>{group.items.length}</span></h2>
          {group.items.map(item => {
            optionIndex += 1
            const index = optionIndex
            const href = group.href(item)
            return <Link id={`global-search-option-${index}`} role="option" aria-selected={activeIndex === index} key={`${group.key}-${item.id}`} to={href}
              className="global-search-result" onMouseEnter={() => setActiveIndex(index)} onClick={event => { event.preventDefault(); chooseResult(href) }}>
              <strong><Highlight text={group.title(item)} query={q}/></strong>
              <small><Highlight text={group.meta(item)} query={q}/></small>
            </Link>
          })}
        </section> : null) : null}
        {!loading && !error && results && flatResults.length === 0 ? <div className="global-search-state"><Search size={19}/><strong>Nenhum resultado para “{q}”</strong><span>Pesquise por número do chamado, cliente, documento, contrato ou oportunidade.</span></div> : null}
        {!loading && q.trim().length >= 2 ? <footer className="global-search-footer">
          <button type="button" onClick={() => chooseResult(`/chamados?busca=${encodeURIComponent(q)}`)}><Search size={14}/>Ver em chamados</button>
          <button type="button" onClick={() => chooseResult('/chamados?novo=1')}><Plus size={14}/>Criar chamado</button>
        </footer> : null}
      </div> : null}
    </form>
  )
}
